
import { createPerformanceLogger } from "@/lib/utils/performance-logger";

const logger = createPerformanceLogger('EnhancedRequestCache');

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  key: string;
}

class EnhancedRequestCache<T> {
  private cache = new Map<string, CacheEntry<T>>();
  private readonly defaultTTL: number;
  private readonly maxSize: number;
  private readonly name: string;

  constructor(name: string, defaultTTL: number = 300000, maxSize: number = 1000) {
    this.name = name;
    this.defaultTTL = defaultTTL;
    this.maxSize = maxSize;
  }

  get(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      logger.debug(`${this.name} cache miss for key: ${key}`);
      return null;
    }

    const now = Date.now();
    if (now - entry.timestamp > this.defaultTTL) {
      logger.debug(`${this.name} cache expired for key: ${key}`);
      this.cache.delete(key);
      return null;
    }

    logger.debug(`${this.name} cache hit for key: ${key}`);
    return entry.data;
  }

  set(key: string, data: T, customTTL?: number): void {
    // Manage cache size
    if (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
      logger.debug(`${this.name} cache evicted oldest entry: ${oldestKey}`);
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      key
    });

    logger.debug(`${this.name} cached data for key: ${key}`);
  }

  delete(key: string): boolean {
    const deleted = this.cache.delete(key);
    if (deleted) {
      logger.debug(`${this.name} manually deleted cache entry: ${key}`);
    }
    return deleted;
  }

  clear(): void {
    const size = this.cache.size;
    this.cache.clear();
    logger.debug(`${this.name} cache cleared, removed ${size} entries`);
  }

  // Clear entries matching a pattern (for user-specific clearing)
  clearPattern(pattern: string): number {
    let cleared = 0;
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        cleared++;
      }
    }
    logger.debug(`${this.name} cleared ${cleared} entries matching pattern: ${pattern}`);
    return cleared;
  }

  // Invalidate cache entries by pattern
  invalidatePattern(pattern: string): number {
    let invalidated = 0;
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        invalidated++;
      }
    }
    logger.debug(`${this.name} invalidated ${invalidated} entries matching pattern: ${pattern}`);
    return invalidated;
  }

  // Force clear with logging
  forceClear(): void {
    const size = this.cache.size;
    this.cache.clear();
    logger.debug(`${this.name} force cleared ${size} cache entries`);
  }

  getStats() {
    return {
      name: this.name,
      size: this.cache.size,
      maxSize: this.maxSize,
      ttl: this.defaultTTL,
      entries: Array.from(this.cache.entries()).map(([key, entry]) => ({
        key,
        timestamp: entry.timestamp,
        age: Date.now() - entry.timestamp
      }))
    };
  }

  // Get entries that will expire soon (within next 60 seconds)
  getExpiringSoon(): string[] {
    const now = Date.now();
    const soonThreshold = 60000; // 1 minute
    const expiring: string[] = [];

    for (const [key, entry] of this.cache.entries()) {
      const timeUntilExpiry = this.defaultTTL - (now - entry.timestamp);
      if (timeUntilExpiry > 0 && timeUntilExpiry < soonThreshold) {
        expiring.push(key);
      }
    }

    return expiring;
  }
}

// Create singleton instances for different cache types
export const weeklyAvailabilityCache = new EnhancedRequestCache('WeeklyAvailability', 300000); // 5 minutes
export const hostSettingsCache = new EnhancedRequestCache('HostSettings', 300000); // 5 minutes
export const calendarSlotsCache = new EnhancedRequestCache('CalendarSlots', 120000); // 2 minutes
export const energyOptimizationCache = new EnhancedRequestCache('EnergyOptimization', 300000); // 5 minutes

// Legacy export for backwards compatibility
export const settingsCache = hostSettingsCache;

// Global cache management functions
export const clearAllAvailabilityCache = (userId?: string) => {
  if (userId) {
    // Clear user-specific cache entries
    const pattern = `_${userId}`;
    weeklyAvailabilityCache.clearPattern(pattern);
    hostSettingsCache.clearPattern(pattern);
    calendarSlotsCache.clearPattern(pattern);
    energyOptimizationCache.clearPattern(pattern);
    logger.debug(`Cleared all availability cache for user: ${userId}`);
  } else {
    // Clear all cache
    weeklyAvailabilityCache.clear();
    hostSettingsCache.clear();
    calendarSlotsCache.clear();
    energyOptimizationCache.clear();
    logger.debug('Cleared all availability cache');
  }
};

// Force clear all caches
export const forceClearAllCaches = (userId?: string) => {
  if (userId) {
    const pattern = `_${userId}`;
    weeklyAvailabilityCache.invalidatePattern(pattern);
    hostSettingsCache.invalidatePattern(pattern);
    calendarSlotsCache.invalidatePattern(pattern);
    energyOptimizationCache.invalidatePattern(pattern);
    logger.debug(`Force cleared all caches for user: ${userId}`);
  } else {
    weeklyAvailabilityCache.forceClear();
    hostSettingsCache.forceClear();
    calendarSlotsCache.forceClear();
    energyOptimizationCache.forceClear();
    logger.debug('Force cleared all caches');
  }
};

export const getAllCacheStats = () => {
  return {
    weeklyAvailability: weeklyAvailabilityCache.getStats(),
    hostSettings: hostSettingsCache.getStats(),
    calendarSlots: calendarSlotsCache.getStats(),
    energyOptimization: energyOptimizationCache.getStats()
  };
};

export const getExpiringSoonEntries = () => {
  return {
    weeklyAvailability: weeklyAvailabilityCache.getExpiringSoon(),
    hostSettings: hostSettingsCache.getExpiringSoon(),
    calendarSlots: calendarSlotsCache.getExpiringSoon(),
    energyOptimization: energyOptimizationCache.getExpiringSoon()
  };
};
